import { Spacing, Typography } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import IconMore from '@persona-id/design-tokens/icons/MoreIcon';
import { compact } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useFormState } from 'react-hook-form';
import { Prompt } from 'react-router';
import { useLocation } from 'react-router-dom-v5-compat';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ApiAttributesBlocklist from 'dashboard/components/ApiAttributesBlocklist';
import AttributeItem from 'dashboard/components/AttributeItem';
import CopyableField from 'dashboard/components/CopyableField';
import EventNameSticker from 'dashboard/components/EventNameSticker';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect from 'dashboard/components/ReactSelect';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import WebhookBlocklist from 'dashboard/components/WebhookBlocklist';
import WebhookEventStatusSticker from 'dashboard/components/WebhookEventStatusSticker';
import WebhookExpiringSecretBanner from 'dashboard/components/WebhookExpiringSecretBanner';
import WebhookStatusSticker from 'dashboard/components/WebhookStatusSticker';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import {
  FormPasswordConfirmationModal,
  FormPasswordConfirmationModalData,
} from 'dashboard/forms/FormPasswordConfirmationModal';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  Backdrop,
  ColumnContainer,
  InfoGrid,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import { Webhook, WebhookStatus } from 'dashboard/models/webhook';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import { FormUpdateWebhookData } from 'dashboard/routes/DashboardWebhooksShowRoute';
import CustomHttpHeaders from 'dashboard/views/DashboardWebhooksShowView/CustomHttpHeaders';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import {
  API_KEY_INFLECTION_LABEL_MAP,
  ApiKeyInflection,
  ApiVersion,
  LATEST_API_VERSION,
} from 'lib/constants/api';
import { EventName } from 'lib/constants/events';
import {
  DATE_AND_TIME_FORMAT,
  DATE_AND_TIME_TZ_FORMAT,
  TIMEZONE_NAME_ABBREVIATED,
} from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';
import isValidJSON from 'lib/utils/formValidation/isValidJSON';
import isValidUrl from 'lib/utils/formValidation/isValidUrl';

interface DashboardWebhooksShowViewProps {
  organization: Organization;
  webhook: Webhook;
  webhookEvents: WebhookEvent[];
  webhookEventsPageSize: number;
  webhookEventsPaginationParams: PaginationParams;
  isLoadingWebhookEvents: boolean;
  handleUpdateWebhook: (
    webhook: Webhook,
    data: FormUpdateWebhookData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleRotateWebhookSecret: (
    webhook: Webhook,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleEnableWebhook: (
    webhook: Webhook,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleDisableWebhook: (
    webhook: Webhook,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleArchiveWebhook: (
    webhook: Webhook,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleShowWebhookEvent: (webhookEvent: WebhookEvent) => void;
  handleWebhookEventsPageLinkClick: (pageSize: number) => void;
  handleWebhookEventsPageSizeClick: (pageNumer: number) => void;
}

const DashboardWebhooksShowView = ({
  organization,
  webhook,
  webhookEvents,
  webhookEventsPageSize,
  webhookEventsPaginationParams,
  isLoadingWebhookEvents,
  handleUpdateWebhook,
  handleRotateWebhookSecret,
  handleEnableWebhook,
  handleDisableWebhook,
  handleArchiveWebhook,
  handleShowWebhookEvent,
  handleWebhookEventsPageLinkClick,
  handleWebhookEventsPageSizeClick,
}: DashboardWebhooksShowViewProps) => {
  // Do not require password confirmation if Organization has SAML enabled
  const requirePasswordConfirmation = !organization.attributes['has-saml-enabled'];

  const canEditWebhook = usePermission(Permission.WebhookWrite);

  const canUseCustomHttpHeadersProductFeature = useProductFeature(
    ProductFeatureName.WebhooksCustomHttpHeaders,
  );
  const canUsePayloadFilterProductFeature = useProductFeature(
    ProductFeatureName.WebhooksPayloadFilters,
  );

  const [
    passwordConfirmationCallback,
    // NOTE: Because this is a function state, need to "wrap" the function call
    // i.e. () => () => {}
    //
    // Reference: https://stackoverflow.com/questions/55621212/is-it-possible-to-react-usestate-in-react
    setPasswordConfirmationCallback,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ] = useState<(meta: FormPasswordConfirmationModalData) => void>(null);

  const [
    isOpenPasswordConfirmationModal,
    { enable: showPasswordConfirmationModal, disable: hidePasswordConfirmationModal },
  ] = useToggle(false);

  const {
    register,
    trigger,
    handleSubmit: handleReactHookFormSubmit,
    control,
    reset,
  } = useFormSecurely<FormUpdateWebhookData>({
    mode: 'onBlur',
    shouldUnregister: false,
  });

  const { isSubmitting, isDirty, errors, dirtyFields } = useFormState({
    control,
  });
  const disabled = isSubmitting || !canEditWebhook;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const hasExpiringSecret = webhook.attributes['secrets'].length > 1;

  useEffect(() => {
    // Reset default values whenever webhook updates
    reset({
      url: webhook.attributes['url'],
      'api-version': webhook.attributes['api-version'],
      'api-key-inflection': webhook.attributes['api-key-inflection'],
      'api-attributes-blocklist': webhook.attributes['api-attributes-blocklist'],
      'included-blocklist': webhook.attributes['included-blocklist'],
      'relationship-blocklist': webhook.attributes['relationship-blocklist'],
      'custom-http-headers': webhook.attributes['custom-http-headers'],
      'file-access-token-expires-in': webhook.attributes['file-access-token-expires-in'],
      'enabled-events': webhook.attributes['enabled-events'],
      'payload-filter': webhook.attributes['payload-filter'],
    });
  }, [reset, webhook]);

  const location = useLocation();
  useEffect(() => {
    // Reset default values whenever tab changes
    reset();
  }, [location.pathname, reset]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const canUpgradeApiVersion = webhook.attributes['api-version'] < LATEST_API_VERSION;
  const apiVersionOptions = Object.values(ApiVersion)
    // don't let user downgrade version
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    .filter((v) => v >= webhook.attributes['api-version'] && v <= LATEST_API_VERSION)
    .sort()
    .reverse()
    .map((v) => {
      return v === LATEST_API_VERSION ? (
        // mark latest version
        <Option value={v} key={v}>
          {v}(Latest)
        </Option>
      ) : (
        <Option value={v} key={v}>
          {v}
        </Option>
      );
    });

  const apiKeyInflectionOptions = Object.values(ApiKeyInflection).map((apiKeyInflection) => (
    <Option value={apiKeyInflection} key={apiKeyInflection}>
      {API_KEY_INFLECTION_LABEL_MAP[apiKeyInflection]}
    </Option>
  ));

  return (
    <>
      <Helmet title={`${webhook.id} - Webhooks`} />
      <InactiveEnvironmentBanner />
      <Header title={<NavBack to='/dashboard/webhooks'>All Webhooks</NavBack>}>
        <HeaderRow>
          <HStack>
            <Permissioned permission={Permission.WebhookWrite}>
              <Button
                variant='primary'
                id='webhook__header__save'
                disabled={disabled || !isDirty}
                onClick={async () => {
                  const result = await trigger();
                  if (result) {
                    if (requirePasswordConfirmation) {
                      setPasswordConfirmationCallback(
                        () => (meta: FormPasswordConfirmationModalData) => {
                          handleReactHookFormSubmit(async (data: FormUpdateWebhookData) => {
                            await handleUpdateWebhook(webhook, data, meta);
                          })();
                        },
                      );
                      showPasswordConfirmationModal();
                    } else {
                      await handleReactHookFormSubmit(async (data: FormUpdateWebhookData) => {
                        await handleUpdateWebhook(webhook, data);
                      })();
                    }
                  }
                }}
              >
                Save
              </Button>

              {webhook.attributes['status'] === WebhookStatus.Disabled && (
                <Button
                  variant='primary'
                  id='webhook__header__enable'
                  onClick={() => {
                    if (requirePasswordConfirmation) {
                      setPasswordConfirmationCallback(
                        () => (meta: FormPasswordConfirmationModalData) => {
                          handleEnableWebhook(webhook, meta);
                        },
                      );
                      showPasswordConfirmationModal();
                    } else {
                      handleEnableWebhook(webhook);
                    }
                  }}
                >
                  Enable
                </Button>
              )}

              {webhook.attributes['status'] === WebhookStatus.Enabled && (
                <Button
                  variant='danger'
                  id='webhook__header__enable'
                  onClick={() => {
                    if (requirePasswordConfirmation) {
                      setPasswordConfirmationCallback(
                        () => (meta: FormPasswordConfirmationModalData) => {
                          handleDisableWebhook(webhook, meta);
                        },
                      );
                      showPasswordConfirmationModal();
                    } else {
                      handleDisableWebhook(webhook);
                    }
                  }}
                >
                  Disable
                </Button>
              )}

              <DropdownMenu
                trigger={
                  <Button
                    id='account-header__button--dropdown'
                    tabIndex={0}
                    variant='basic'
                    prefixIcon={<IconMore />}
                  >
                    More
                  </Button>
                }
                align='end'
              >
                {[
                  {
                    id: 'webhook__header__rotate-secret',
                    name: 'webhook__header__rotate-secret',
                    'data-test': 'webhook__header__rotate-secret',
                    onClick: () => {
                      if (requirePasswordConfirmation) {
                        setPasswordConfirmationCallback(
                          () => (meta: FormPasswordConfirmationModalData) => {
                            handleRotateWebhookSecret(webhook, meta);
                          },
                        );
                        showPasswordConfirmationModal();
                      } else {
                        handleRotateWebhookSecret(webhook);
                      }
                    },
                    label: 'Rotate secret',
                    variant: 'basic' as const, // danger primary
                  },
                  {
                    id: 'webhook__header__archive',
                    name: 'webhook__header__archive',
                    'data-test': 'webhook__header__archive',
                    onClick: () => {
                      if (requirePasswordConfirmation) {
                        setPasswordConfirmationCallback(
                          () => (meta: FormPasswordConfirmationModalData) => {
                            handleArchiveWebhook(webhook, meta);
                          },
                        );
                        showPasswordConfirmationModal();
                      } else {
                        handleArchiveWebhook(webhook);
                      }
                    },
                    label: 'Archive',
                    disabled: webhook.attributes['status'] !== WebhookStatus.Disabled,
                    variant: 'danger' as const,
                  },
                ].map((item) => (
                  <DropdownMenu.Item
                    data-test={item['data-test']}
                    id={item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      item.onClick();
                    }}
                    key={item.id}
                    variant={item.variant}
                  >
                    {item.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu>
            </Permissioned>
          </HStack>
        </HeaderRow>
      </Header>
      <Backdrop>
        <Container>
          <Prompt
            when={Object.keys(dirtyFields).length > 0 && !isSubmitting} // https://github.com/react-hook-form/react-hook-form/issues/3213
            message='You have unsaved changes. Are you sure you want to leave?'
          />
          <ColumnContainer>
            <MainColumn>
              <RoutedTabbedSection
                dataTest='webhook__tabs'
                tabs={compact([
                  {
                    name: 'Overview',
                    path: '/',
                    contents: (
                      <>
                        <Card>
                          <Card.Body>
                            <Card.Title>Webhook secret</Card.Title>
                            <Paragraph color='primary'>
                              Use this secret to compute request signatures and authenticate webhook
                              calls.{' '}
                              <a
                                href='https://docs.withpersona.com/docs/webhooks-best-practices'
                                target='_blank'
                                rel='noreferrer'
                              >
                                Learn more
                              </a>
                              .
                            </Paragraph>
                            <CopyableField
                              id='webhook-show__secret'
                              data-test='webhook-show__secret'
                              type='password'
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              value={webhook.attributes['secrets'][0]}
                            />
                            {hasExpiringSecret && (
                              <WebhookExpiringSecretBanner
                                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                value={webhook.attributes['secrets'][1]}
                              />
                            )}
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>Enabled events</Card.Title>

                            <Controller
                              control={control}
                              name='enabled-events'
                              rules={{
                                validate: (v) => {
                                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                  if (v.length > 0) {
                                    return true;
                                  } else {
                                    return 'Please choose one or more events';
                                  }
                                },
                              }}
                              render={({ field: { onChange, onBlur, value, ref } }) => (
                                <ReactSelect
                                  inputRef={ref}
                                  value={value}
                                  options={Object.values(EventName)
                                    .filter(
                                      // TODO (FLE-288): remove this filter when `inquiry-run.conditional-routed` is removed
                                      (event) => event !== EventName.InquiryRunConditionalRouted,
                                    )
                                    .map((key) => ({
                                      label: key,
                                      value: key,
                                    }))}
                                  disabled={disabled}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  placeholder='No events selected'
                                  hasError={!!errors['enabled-events']}
                                  portal={true}
                                  isMulti={true}
                                  isSearchable={true}
                                />
                              )}
                            />
                            {errors['enabled-events'] && (
                              <FormError>{errors['enabled-events'].message}</FormError>
                            )}
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>API version</Card.Title>
                            <Paragraph color='primary'>
                              Currently, your requests default to use API version{' '}
                              <strong>{webhook.attributes['api-version']}</strong>.
                            </Paragraph>

                            {canUpgradeApiVersion && (
                              <Controller
                                name='api-version'
                                control={control}
                                render={({ field }) => (
                                  <Select {...field} disabled={disabled}>
                                    {apiVersionOptions}
                                  </Select>
                                )}
                              />
                            )}
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>Key inflection</Card.Title>
                            <Paragraph color='primary'>
                              Configure the member name formatting for all webhook payloads.{' '}
                              <a
                                href='https://docs.withpersona.com/reference/key-inflection'
                                target='_blank'
                                rel='noreferrer'
                              >
                                Learn more
                              </a>
                              .
                            </Paragraph>

                            <Controller
                              name='api-key-inflection'
                              control={control}
                              render={({ field }) => (
                                <Select {...field} disabled={disabled}>
                                  {apiKeyInflectionOptions}
                                </Select>
                              )}
                            />
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>File Access Token Expiry (seconds)</Card.Title>
                            <Paragraph color='primary'>
                              Configure how long file URLs returned via API request are valid.{' '}
                              <a
                                href='https://docs.withpersona.com/reference/downloading-files'
                                target='_blank'
                                rel='noreferrer'
                              >
                                Learn more
                              </a>
                              .
                            </Paragraph>

                            <Controller
                              name='file-access-token-expires-in'
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <Input
                                  role='input'
                                  type='number'
                                  placeholder="Number of seconds for a file's access token to be valid"
                                  id='file-access-token-expires-in__input'
                                  value={value}
                                  onChange={(e) => {
                                    onChange(e.target.value);
                                  }}
                                />
                              )}
                            />
                          </Card.Body>
                        </Card>
                      </>
                    ),
                  },
                  {
                    name: 'Blocklist',
                    path: '/blocklist',
                    contents: (
                      <Card key='blocklist'>
                        <Card.Body>
                          <Card.Title>Payload Blocklist</Card.Title>
                          <Paragraph color='primary'>
                            Filter specific information from the payload for Webhook Events
                            triggered by this Webhook.
                          </Paragraph>
                          <Spacer height={1} />

                          <Card.SectionHeader>
                            Block <code>relationships</code> and <code>included</code> objects
                          </Card.SectionHeader>
                          <Paragraph color='primary'>
                            By default, Webhook Event payloads will include all of the trigger
                            object’s attributes within <code>attributes</code> , related objects in{' '}
                            <code>relationships</code>, and representations of related objects in{' '}
                            <code>included</code>. Block everything within{' '}
                            <code>relationships</code> and <code>included</code> from Webhook Event
                            payloads below. For more information on the structure of payloads, see{' '}
                            <a
                              href='https://docs.withpersona.com/reference/serialization'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Serialization
                            </a>
                            .
                          </Paragraph>
                          <WebhookBlocklist
                            control={control}
                            disabled={disabled}
                            errors={[
                              errors['included-blocklist']?.message,
                              errors['relationship-blocklist']?.message,
                            ].filter((message) => message != null)}
                          />

                          <Card.SectionHeader>Block Attributes</Card.SectionHeader>
                          <Paragraph color='primary'>
                            By default, Webhook Event payloads will include all of the trigger
                            object’s attributes within <code>attributes</code>. Block individual
                            attributes or set dynamic rules using wildcard characters (*) by adding
                            to the list below. For more information, see{' '}
                            <a
                              href='https://docs.withpersona.com/docs/webhook-blocklists#attribute-blocklist'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Webhook Attribute Blocklists
                            </a>
                            .
                          </Paragraph>
                          <Controller
                            control={control}
                            name='api-attributes-blocklist'
                            render={({ field: { onChange, onBlur, value } }) => (
                              <ApiAttributesBlocklist
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                disabled={disabled}
                                error={errors['api-attributes-blocklist']?.message}
                              />
                            )}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                  canUsePayloadFilterProductFeature && {
                    name: 'Event filter',
                    path: '/event-filter',
                    contents: (
                      <Card key='payload-filter'>
                        <Card.Body>
                          <Card.Title>Event filter</Card.Title>
                          <Paragraph color='primary'>
                            Event filters allow developers to be more selective with which Events
                            they receive. If the filter is a strict subset of the payload data for
                            an Event, the Event will go through.
                          </Paragraph>
                          <Spacer height={1} />
                          <Paragraph color='primary'>
                            Read more about webhook event filtering{' '}
                            <a
                              href='https://docs.withpersona.com/docs/webhook-event-filters'
                              target='_blank'
                              rel='noreferrer'
                            >
                              here
                            </a>
                            .
                          </Paragraph>

                          {canEditWebhook ? (
                            <Controller
                              control={control}
                              name='payload-filter'
                              render={({ field: { onChange, onBlur, value } }) => (
                                <EditorContainer>
                                  <CodeEditor
                                    language='json'
                                    code={JSON.stringify(value, null, 2)}
                                    hasError={!!errors['payload-filter']}
                                    readOnly={disabled}
                                    onChange={(v) => {
                                      if (isValidJSON(v)) {
                                        onChange(JSON.parse(v));
                                      }
                                    }}
                                    onBlur={onBlur}
                                  />
                                </EditorContainer>
                              )}
                            />
                          ) : (
                            <CodeHighlight>
                              {JSON.stringify(webhook.attributes['payload-filter'], null, 2)}
                            </CodeHighlight>
                          )}

                          {errors['payload-filter'] && (
                            <FormError>{errors['payload-filter'].message}</FormError>
                          )}
                        </Card.Body>
                      </Card>
                    ),
                  },
                  canUseCustomHttpHeadersProductFeature && {
                    name: 'Custom HTTP Headers',
                    path: '/custom-http-headers',
                    contents: (
                      <Card key='custom-http-headers'>
                        <Card.Body>
                          <Card.Title>Custom HTTP Headers</Card.Title>
                          <Paragraph color='primary'>
                            Set custom HTTP headers to be included in all webhook requests.
                          </Paragraph>
                          <Controller
                            control={control}
                            name='custom-http-headers'
                            render={({ field: { onChange, onBlur, value } }) => (
                              <CustomHttpHeaders
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                disabled={disabled}
                                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                error={errors['custom-http-headers']?.message.toString()}
                              />
                            )}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                  {
                    name: 'Webhook events',
                    path: '/webhook-events',
                    contents: (
                      <Card key='webhook-events'>
                        <Card.Body>
                          <Card.Title>Webhook events</Card.Title>

                          <StyledBanner variant='warning'>
                            <CircleInfoIcon />
                            Webhook events are subject to a 90 day retention policy. They are
                            deleted 90 days after their creation and then are inaccessible via the
                            Dashboard or API.
                          </StyledBanner>
                          <Spacer height={1} />

                          <TableLayout
                            dataTest='webhook-events__table'
                            isEmpty={webhookEvents.length === 0}
                            isLoading={isLoadingWebhookEvents}
                            headerTR={
                              <>
                                <TableTH>Status (Code)</TableTH>
                                <TableTH>Event name</TableTH>
                                <TableTH>{`Last attempted at (${DateTime.local().toFormat(
                                  TIMEZONE_NAME_ABBREVIATED,
                                )})`}</TableTH>
                                <TableTH>Attempts left</TableTH>
                              </>
                            }
                            nullState={
                              <TableNullState>Webhook has not been triggered.</TableNullState>
                            }
                          >
                            {webhookEvents.map((webhookEvent) => {
                              return (
                                <TableTR
                                  key={webhookEvent.id}
                                  data-test={`webhook-events__table__row--${webhookEvent.id}`}
                                  id={`webhook-events__table__row--${webhookEvent.id}`}
                                  onClick={() => {
                                    handleShowWebhookEvent(webhookEvent);
                                  }}
                                >
                                  <TableTD>
                                    <WebhookEventStatusSticker webhookEvent={webhookEvent} />
                                  </TableTD>
                                  <TableTD>
                                    <EventNameSticker
                                      eventName={webhookEvent.attributes['event-name']}
                                    />
                                  </TableTD>
                                  <TableTD>
                                    <AttributeItem
                                      value={
                                        webhookEvent.attributes['last-attempted-at'] &&
                                        DateTime.fromISO(
                                          webhookEvent.attributes['last-attempted-at'],
                                        ).toFormat(DATE_AND_TIME_FORMAT)
                                      }
                                      width='100%'
                                    />
                                  </TableTD>
                                  <TableTD>{webhookEvent.attributes['attempts-left']}</TableTD>
                                </TableTR>
                              );
                            })}
                          </TableLayout>
                          <Pagination
                            pageSize={webhookEventsPageSize}
                            paginationParams={webhookEventsPaginationParams}
                            handlePageSizeClick={handleWebhookEventsPageSizeClick}
                            handlePageLinkClick={handleWebhookEventsPageLinkClick}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                ])}
              />
            </MainColumn>
            <RightColumn>
              <Card>
                <Card.Body>
                  <Card.Title>Info</Card.Title>

                  <InfoGrid>
                    <AttributeItem
                      title='Status'
                      value={<WebhookStatusSticker webhook={webhook} />}
                      width='100%'
                    />

                    <AttributeItem
                      title='URL'
                      value={
                        <>
                          <Input
                            {...register('url', {
                              validate: (v) => {
                                if (isValidUrl(v)) {
                                  return true;
                                } else {
                                  return 'Please enter a valid HTTPS URL';
                                }
                              },
                            })}
                            id='webhook__info__input--url'
                            type='text'
                            placeholder='Name'
                            hasError={!!errors['url']}
                            disabled={disabled}
                          />
                          {errors['url'] && <FormError>{errors['url'].message}</FormError>}
                        </>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Created at'
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      value={DateTime.fromISO(webhook.attributes['created-at']).toFormat(
                        DATE_AND_TIME_TZ_FORMAT,
                      )}
                      width='100%'
                    />
                  </InfoGrid>
                </Card.Body>
              </Card>
            </RightColumn>
          </ColumnContainer>

          <FormPasswordConfirmationModal
            isOpen={isOpenPasswordConfirmationModal}
            handleSubmit={passwordConfirmationCallback}
            handleCloseModal={hidePasswordConfirmationModal}
          />
        </Container>
      </Backdrop>
    </>
  );
};

const HeaderRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const EditorContainer = styled.div`
  height: 500px;
`;

const StyledBanner = styled(Banner)`
  ${Typography.TextMedium};
  padding: ${Spacing[4]};
`;

export default DashboardWebhooksShowView;
